import { internalFetch } from '../utils/fetchHelper';
import { IPagedCollection } from '@moko/utils';

export interface IReferenceValue {
  id: number;
  key: string;
  value: string;
  parentRef: string | null;
  parentKey: string | null;
  sortOrder: number | null;
}

export interface ICreateReferenceValue {
  refName: string;
  key: string;
  value: string;
  parentRef: string;
  parentKey: string;
}

export interface ICreateReferenceValueResponse {
  referenceValueId: number;
}

export interface IUpdateReferenceValue {
  value: string;
  parentRef: string;
  parentKey: string;
}

export interface IImportReferenceValuesRequest {
  csvContent: string;
}

export interface IImportReferenceValuesResponse {
  createdCount: number;
  updatedCount: number;
  errors: string[];
}

export const getReferenceValues = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  refName: string,
  orderBy?: string,
  search?: string
): Promise<IPagedCollection<IReferenceValue>> => {
  const ordering = orderBy || 'sortOrder';
  const page: IPagedCollection<IReferenceValue> = await internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/referenceValues?refName=${refName}&pageSize=${pageSize}&pageNo=${pageNo}&orderBy=${encodeURIComponent(
      ordering
    )}${search ? `&search=${search}` : ''}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
  if (ordering !== 'sortOrder' && ordering !== 'sortOrder Desc') return page;
  const direction = ordering.endsWith('Desc') ? -1 : 1;
  return {
    ...page,
    items: [...(page.items ?? [])].sort((left, right) => {
      const leftOrder = left.sortOrder ?? Number.POSITIVE_INFINITY;
      const rightOrder = right.sortOrder ?? Number.POSITIVE_INFINITY;
      if (leftOrder !== rightOrder) {
        return (leftOrder - rightOrder) * direction;
      }
      return (left.id - right.id) * direction;
    }),
  };
};

export const orderReferenceValues = async (
  abortController: AbortController,
  refName: string,
  referenceValueIds: number[]
): Promise<void> => {
  await internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/referenceValues/order`,
    'PUT',
    abortController,
    JSON.stringify({ refName, referenceValueIds })
  );
};

export const getReferences = async (
  abortController: AbortController,
  _entityType?: string
): Promise<string[]> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/references?code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getReferenceValue = async (
  abortController: AbortController,
  refValueId?: number
): Promise<string[]> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/referenceValue?referenceValueId=${refValueId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getAutoCompleteValues = async (
  abortController: AbortController,
  refName: string,
  text: string,
  dependees?: string
): Promise<{ key: string; value: string }[]> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/autoCompleteValues?refName=${refName}${
      dependees ? `&dependees=${dependees}` : ''
    }&text=${text}&code=${import.meta.env.VITE_BACKEND_API_KEY}`,
    'GET',
    abortController
  );
};

export const editReferenceValue = async (
  abortController: AbortController,
  referenceValueId: number,
  referenceValue: IUpdateReferenceValue
): Promise<number> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/referenceValue?referenceValueId=${referenceValueId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'PUT',
    abortController,
    JSON.stringify(referenceValue)
  );
};

export const deleteRefValue = async (
  abortController: AbortController,
  referenceValueId: number
): Promise<number> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/referenceValue?referenceValueId=${referenceValueId}`,
    'DELETE',
    abortController
  );
};

export const createReferenceValue = async (
  abortController: AbortController,
  referenceValue: ICreateReferenceValue
): Promise<ICreateReferenceValueResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/referenceValue`,
    'POST',
    abortController,
    JSON.stringify(referenceValue)
  );
};

export const exportReferenceValues = async (
  abortController: AbortController,
  refNames: string[]
): Promise<string> => {
  const response: Response = await internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/exportReferenceValues?refName=${encodeURIComponent(
      refNames.join(',')
    )}&code=${import.meta.env.VITE_BACKEND_API_KEY}`,
    'GET',
    abortController,
    undefined,
    true
  );

  return response.text();
};

export const importReferenceValues = async (
  abortController: AbortController,
  request: IImportReferenceValuesRequest
): Promise<IImportReferenceValuesResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/importReferenceValues`,
    'POST',
    abortController,
    JSON.stringify(request)
  );
};
